/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose, getDisplayName, setDisplayName } from 'recompose';

import Imgix from 'js/components/Imgix';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import type { Theme } from '@coursera/cds-core';
import { Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import { getProductCardDisplayProps } from 'bundles/browse/utils';
import { EnrollmentChoiceModalProductDescriptionQuery } from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalQuery';
import type {
  EnrollmentChoiceModalProductDescriptionQuery_XdpV1Resource_get_xdpMetadata_XdpV1_cdpMetadataMember as CdpMetadataMember,
  EnrollmentChoiceModalProductDescriptionQuery as EnrollmentChoiceModalProductDescriptionQueryType,
  EnrollmentChoiceModalProductDescriptionQueryVariables,
  EnrollmentChoiceModalProductDescriptionQuery_XdpV1Resource_get_xdpMetadata_XdpV1_sdpMetadataMember as SdpMetadataMember,
} from 'bundles/enterprise-legacy-learner-home/components/__generated__/EnrollmentChoiceModalProductDescriptionQuery';
import {
  PRODUCT_TYPES,
  PRODUCT_TYPES_SERVER,
} from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import Skeleton from 'bundles/unified-home-common/components/shared/Skeleton';

type PropsFromCaller = { productId?: string; productType?: string };

type PropsFromGraphql = {
  loading: boolean;
  partnerName?: string;
  productLogo?: string;
  productName?: string;
  productTypeText?: string;
};

type Props = PropsFromCaller & PropsFromGraphql;

const styles = {
  logo: {
    borderRadius: 12,
  },
  logoSkeleton: (theme: Theme) => ({
    backgroundColor: 'var(--cds-color-grey-25)',
    borderRadius: 12,
    width: 80,
    height: 80,
  }),
  productSection: (theme: Theme) => ({
    display: 'flex',
    flexFlow: 'row nowrap',
    borderTop: `1px solid var(--cds-color-grey-300)`,
    borderBottom: `1px solid var(--cds-color-grey-300)`,
    margin: 'var(--cds-spacing-400) var(--cds-spacing-600) 0 var(--cds-spacing-600)',
    padding: 'var(--cds-spacing-200) 0',
    alignItems: 'center' as const,
    [breakpoints.down('xs')]: {
      margin: 'var(--cds-spacing-300) var(--cds-spacing-200) 0 var(--cds-spacing-200)',
      padding: 'var(--cds-spacing-200) 0',
    },
  }),
  productDescription: (theme: Theme) => ({
    marginLeft: 'var(--cds-spacing-200)',
    flexGrow: 1,
    overflow: 'hidden' as const,
  }),
  pillTypography: {
    textTransform: 'uppercase' as const,
  },
  pill: (theme: Theme) => ({
    display: 'inline-block' as const,
    backgroundColor: 'var(--cds-color-grey-50)',
    borderRadius: 4,
    paddingLeft: 6,
    paddingRight: 6,
  }),
  truncateText: {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
  },
};

export const EnrollmentChoiceModalProductDescription = ({
  loading,
  partnerName,
  productLogo,
  productName,
  productTypeText,
}: Props) => {
  const theme = useTheme();

  const shouldRenderNothing = !loading && !productName;
  if (shouldRenderNothing) {
    return null;
  }

  return (
    <div css={styles.productSection(theme)}>
      {productLogo && productName ? (
        <Imgix css={styles.logo} src={productLogo} alt={productName} maxWidth={80} maxHeight={80} />
      ) : (
        <Skeleton bonusCSS={styles.logoSkeleton(theme)} />
      )}
      <div css={styles.productDescription(theme)}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography variant="h2" component="p" css={styles.truncateText}>
          {productName || <Skeleton width={600} />}
        </Typography>
        <Typography2 component="p" variant="bodyPrimary" css={styles.truncateText}>
          {partnerName || <Skeleton width={200} />}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" css={styles.pillTypography}>
          {productTypeText ? (
            <span css={styles.pill(theme)}>{productTypeText}</span>
          ) : (
            <Skeleton bonusCSS={styles.pill(theme)} width={100} />
          )}
        </Typography2>
      </div>
    </div>
  );
};

const enhance = compose<Props, PropsFromCaller>(
  setDisplayName(getDisplayName(EnrollmentChoiceModalProductDescription)),
  graphql<
    PropsFromCaller,
    EnrollmentChoiceModalProductDescriptionQueryType,
    EnrollmentChoiceModalProductDescriptionQueryVariables,
    PropsFromGraphql
  >(EnrollmentChoiceModalProductDescriptionQuery, {
    skip: ({ productId, productType }) => !productId || !productType,
    options: ({ productId, productType }) => {
      const productType0 =
        productType === PRODUCT_TYPES.SPECIALIZATION
          ? PRODUCT_TYPES_SERVER.SPECIALIZATION
          : PRODUCT_TYPES_SERVER.COURSE;
      return {
        variables: {
          // See above skip().

          productId: tupleToStringKey([productType0, productId!]),
        },
      };
    },
    props: ({ data }) => {
      const xdpMetadata = data?.XdpV1Resource?.get?.xdpMetadata;
      const sdpMetadata = (xdpMetadata as SdpMetadataMember | undefined | null)?.sdpMetadata;
      const cpdMetadata = (xdpMetadata as CdpMetadataMember | undefined | null)?.cdpMetadata;
      const isSpecialization = xdpMetadata?.__typename === 'XdpV1_sdpMetadataMember';
      const isRhymeProject = cpdMetadata?.courseTypeMetadata?.__typename === 'XdpV1_rhymeProjectMember';
      const displayProps = data?.loading
        ? undefined
        : getProductCardDisplayProps({ isRhymeProject, courseIds: sdpMetadata?.courseIds }, isSpecialization);
      return {
        loading: data?.loading ?? true,
        error: Boolean(data?.error),
        productName: sdpMetadata?.name ?? cpdMetadata?.name,
        partnerName: sdpMetadata?.partners?.[0]?.name ?? cpdMetadata?.partners?.[0]?.name,
        productLogo: sdpMetadata?.certificateLogo ?? cpdMetadata?.photoUrl ?? undefined,
        productTypeText: displayProps?.labelAsText,
      };
    },
  })
);

export default enhance(EnrollmentChoiceModalProductDescription);
